import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AlertCircle, Calendar, Clock, DollarSign, ChevronDown, ChevronRight, ExternalLink, ShieldCheck, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ISSUE_META = {
  missing_amount: { title: "Missing amount", icon: DollarSign, color: "text-amber-600 bg-amber-50" },
  missing_close_date: { title: "Missing close date", icon: Calendar, color: "text-blue-600 bg-blue-50" },
  past_close_date_open: { title: "Close date in the past", icon: Clock, color: "text-red-600 bg-red-50" },
  stale_no_activity: { title: "No activity 60+ days", icon: AlertCircle, color: "text-slate-700 bg-slate-100" },
};

function formatMoney(v, currency) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${currency || ""} ${v}`;
  }
}

export default function HealthPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState("");
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const [{ data: hc }, { data: st }] = await Promise.all([
        api.get("/health-check"),
        api.get("/hubspot/status"),
      ]);
      setData(hc);
      setStatus(st);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const triggerResync = async () => {
    setResyncing(true);
    setResyncMsg("");
    try {
      await api.post("/hubspot/resync");
      setResyncMsg("Sync started. This may take a moment — refresh shortly.");
    } catch (e) {
      setResyncMsg(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResyncing(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid="health-error">
        <div className="text-sm text-red-600">{err}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="health-loading">
        Loading data health…
      </div>
    );
  }

  if (data.empty) {
    const isConnected = !!status?.connected;
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid="health-empty-state">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center">
          <ShieldCheck className="h-10 w-10 text-slate-400" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">
            {isConnected ? "No snapshot yet" : "No deals synced yet"}
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm">
            {isConnected
              ? "HubSpot is connected but we don't have a deal snapshot for this account yet. Trigger a re-sync to pull your deals now."
              : "Once your first HubSpot sync completes, we'll compute your pipeline's data health score right here."}
          </p>
          <div className="mt-6 flex gap-3">
            {isConnected ? (
              <button
                onClick={triggerResync}
                disabled={resyncing}
                data-testid="health-resync-cta"
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${resyncing ? "animate-spin" : ""}`} />
                {resyncing ? "Syncing…" : "Re-sync now"}
              </button>
            ) : (
              <button
                onClick={() => nav("/dashboard/connect")}
                data-testid="health-connect-cta"
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                Connect HubSpot
              </button>
            )}
            <button
              onClick={load}
              data-testid="health-refresh-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Refresh
            </button>
          </div>
          {resyncMsg && (
            <div className="mt-4 text-xs text-slate-500" data-testid="health-resync-msg">
              {resyncMsg}
            </div>
          )}
        </div>
      </div>
    );
  }

  const scoreColor =
    data.score == null
      ? "text-slate-400"
      : data.score >= 80
      ? "text-emerald-600"
      : data.score >= 50
      ? "text-amber-600"
      : "text-red-600";

  return (
    <div className="max-w-7xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="health-page">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Step 3</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Data Health Check
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl">
            The percentage of your <em>open pipeline value</em> where the amount, close date, and
            recent activity are all present. Snapshot: {data.snapshot_date}.
          </p>
          <SyncChip
            lastSyncAt={status?.last_sync_at}
            onResync={triggerResync}
            resyncing={resyncing}
          />
          {resyncMsg && (
            <div className="mt-2 text-xs text-slate-500" data-testid="health-header-resync-msg">
              {resyncMsg}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-4 flex items-center gap-6" data-testid="health-hero-score">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Health Score</div>
            <div className={`mt-1 text-5xl font-extrabold tracking-tighter ${scoreColor} number-tabular`}>
              {data.score != null ? `${data.score}%` : "—"}
            </div>
          </div>
          <div className="border-l border-slate-200 pl-6 text-sm">
            <div className="text-slate-500">Open deals</div>
            <div className="font-semibold text-slate-900 number-tabular">{data.open_deal_count}</div>
            <div className="mt-2 text-slate-500">Open pipeline value</div>
            <div className="font-semibold text-slate-900 number-tabular">
              {formatMoney(data.total_open_value)}
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.keys(ISSUE_META).map((k) => {
          const meta = ISSUE_META[k];
          const Icon = meta.icon;
          const issue = data.issues[k];
          const isOpen = openKey === k;
          return (
            <div key={k} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`issue-card-${k}`}>
              <button
                onClick={() => setOpenKey(isOpen ? null : k)}
                data-testid={`issue-toggle-${k}`}
                className="w-full p-6 text-left flex items-start gap-4 hover:bg-slate-50/60 transition-colors"
              >
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${meta.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">{meta.title}</div>
                  <div className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900 number-tabular">
                    {issue.count}
                  </div>
                </div>
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-slate-400 mt-2" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-400 mt-2" />
                )}
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 max-h-72 overflow-y-auto" data-testid={`issue-list-${k}`}>
                  {issue.deals.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500">
                      Nothing here — you&apos;re all clear.
                    </div>
                  ) : (
                    issue.deals.slice(0, 100).map((d) => (
                      <a
                        key={d.deal_id}
                        href={d.hubspot_url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`issue-deal-${k}-${d.deal_id}`}
                        className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 text-sm hover:bg-slate-50 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {d.dealname || "Untitled deal"}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {d.amount != null ? formatMoney(d.amount, d.currency) : "no amount"}
                            {d.closedate ? ` · closes ${new Date(d.closedate).toLocaleDateString()}` : ""}
                          </div>
                        </div>
                        <ExternalLink className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      </a>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-xs text-slate-500">
        Data Health Score = the % of your open pipeline <em>value</em> that has an amount, a close
        date, <em>and</em> activity in the last 60 days.
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "unknown";
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 45) return "just now";
  if (diffSec < 90) return "1 min ago";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return `${day} d ago`;
}

function SyncChip({ lastSyncAt, onResync, resyncing }) {
  return (
    <div
      className="mt-4 inline-flex items-center gap-3 rounded-full bg-white ring-1 ring-slate-200 pl-3 pr-1 py-1 text-xs text-slate-600 shadow-sm"
      data-testid="health-sync-chip"
    >
      <span className="flex items-center gap-1.5">
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            lastSyncAt ? "bg-emerald-500" : "bg-slate-300"
          }`}
        />
        <span>
          Last synced{" "}
          <span className="font-semibold text-slate-900" data-testid="health-sync-chip-time">
            {timeAgo(lastSyncAt)}
          </span>
        </span>
      </span>
      <span className="text-slate-300">·</span>
      <button
        onClick={onResync}
        disabled={resyncing}
        data-testid="health-sync-chip-resync"
        className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2.5 py-1 text-xs font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
      >
        <RefreshCw className={`h-3 w-3 ${resyncing ? "animate-spin" : ""}`} />
        {resyncing ? "Syncing" : "Re-sync"}
      </button>
    </div>
  );
}
